import type { IconProps } from '@phosphor-icons/react'
import type { ComponentType, ElementType } from 'react'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

import { cx } from '../../cx'
import type { ButtonAsProps } from '../../polymorphic-props'
import { BaseButton } from './button'

export type IconButtonProps = VariantProps<typeof buttonVariants> & {
  'Icon': ComponentType<IconProps>
  'aria-label': string
  'iconClassName'?: string
  'iconWeight'?: IconProps['weight']
}

const buttonVariants = tv({
  base: 'relative inline-grid cursor-pointer place-content-center gap-2 rounded-2xl transition-colors',
  variants: {
    variant: {
      blank: 'text-primary bg-white',
      blue: 'bg-lightblue text-primary',
      transparent: 'text-primary bg-transparent',
      faded: 'bg-white/75 text-primary hover:bg-white',
      withBorder: 'border-primary/15 border',
    },
    size: {
      icon: 'm-0 size-auto p-0',
      small: 'size-8 rounded-lg p-1',
      medium: 'size-12 px-6 py-4',
    },
    weight: {
      solid: 'shadow-elevation3 disabled:shadow-none',
      ghost: 'font-normal',
    },
    darkBg: { true: 'hover:layer-bg-hover-dark active:layer-bg-active-dark' },
    disabled: { true: 'pointer-events-none opacity-30' },
    iconOnly: { true: 'border-none bg-transparent', false: '' },
  },
  compoundVariants: [
    {
      iconOnly: false,
      class: 'hover:layer-bg-hover-light active:layer-bg-active-light',
    },
  ],
  defaultVariants: {
    variant: 'blank',
    size: 'medium',
    weight: 'solid',
    disabled: false,
    darkBg: false,
    iconOnly: false,
  },
})

const iconSizes = {
  icon: 'w-6 h-6',
  mini: 'w-4 h-4',
  xs: 'size-5',
  small: 'w-6 h-6',
  medium: 'w-8 h-8',
}

export function IconButton<TAs extends ElementType>(props: IconButtonProps & ButtonAsProps<TAs>) {
  const {
    ref,
    disabled,
    size = 'medium',
    className,
    iconClassName,
    iconWeight,
    weight = 'ghost',
    iconOnly = false,
    Icon,
    ...rest
  } = props

  return (
    <BaseButton
      ref={ref}
      className={cx(buttonVariants({ ...rest, size, disabled, weight, iconOnly }), className)}
      disabled={disabled || false}
      {...rest}
    >
      <Icon className={cx(iconSizes[size], iconClassName)} weight={iconWeight} />
    </BaseButton>
  )
}
